import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, AuthContextType, RegisterData } from '../types';
import { supabase } from '../lib/supabase';
import { authService } from '../services/authService';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authInProgress, setAuthInProgress] = useState(false);

  useEffect(() => {
    console.log('🚀 AuthContext initializing...');
    // Get initial session
    authService.getCurrentUser().then(user => {
      console.log('✅ Initial user loaded:', user ? { id: user.id, username: user.username } : null);
      setUser(user);
      setLoading(false);
    }).catch((err) => {
      console.log('❌ Failed to load initial user:', err);
      setUser(null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = authService.onAuthStateChange((user) => {
      console.log('🔄 Auth state changed:', user ? { id: user.id, username: user.username } : null);
      setUser(user);
      setAuthInProgress(false); // Reset auth in progress when state changes
    });

    return () => subscription.unsubscribe();
  }, []);

  // Heartbeat / Inactivity Monitor
  useEffect(() => {
    if (!user) return;

    // 24 hours in milliseconds
    const INACTIVITY_LIMIT = 24 * 60 * 60 * 1000;
    // Throttle activity updates (every 30 seconds)
    const ACTIVITY_UPDATE_THROTTLE = 30000;
    // Throttle session refresh (every 10 minutes)
    const SESSION_REFRESH_THROTTLE = 10 * 60 * 1000;

    const updateActivity = async () => {
      const now = Date.now();
      const last = parseInt(localStorage.getItem('lastActivity') || '0');
      const lastRefresh = parseInt(localStorage.getItem('lastSessionRefresh') || '0');

      if (now - last > ACTIVITY_UPDATE_THROTTLE) {
        localStorage.setItem('lastActivity', now.toString());

        // Refresh session if needed to keep Supabase token alive while active
        if (now - lastRefresh > SESSION_REFRESH_THROTTLE) {
           localStorage.setItem('lastSessionRefresh', now.toString());
           const { error } = await supabase.auth.getSession(); 
           if (error) console.error('Error refreshing session heartbeat:', error);
        }
      }
    };

    const checkInactivity = () => {
      const now = Date.now();
      const last = parseInt(localStorage.getItem('lastActivity') || now.toString());
      
      // If inactive for longer than limit
      if (now - last > INACTIVITY_LIMIT) {
        console.log('💤 User inactive for 24h, logging out...');
        logout();
      }
    };

    // Set initial activity if missing
    if (!localStorage.getItem('lastActivity')) {
      updateActivity();
    }

    // Listeners for user activity
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => window.addEventListener(event, updateActivity));

    // Check interval (every 1 minute)
    const intervalId = setInterval(checkInactivity, 60 * 1000);

    return () => {
      events.forEach(event => window.removeEventListener(event, updateActivity));
      clearInterval(intervalId);
    };
  }, [user]);

  const refreshUser = async () => {
    try {
      const updatedUser = await authService.getCurrentUser();
      setUser(updatedUser);
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };
  const login = async (email: string, password: string) => {
    // Prevent concurrent login attempts
    if (authInProgress) {
      return;
    }
    
    setAuthInProgress(true);
    try {
      await authService.signIn(email, password);
      const user = await authService.getCurrentUser();
      localStorage.setItem('lastActivity', Date.now().toString()); // Reset activity timer
      setUser(user);
    } catch (error) {
      setAuthInProgress(false);
      throw error;
    }
    // Don't set authInProgress to false here - let the auth state change handle it
  };

  const register = async (userData: RegisterData) => {
    // Prevent concurrent registration attempts
    if (authInProgress) {
      return;
    }
    
    setAuthInProgress(true);
    try {
      await authService.signUp(userData);
      // User will be set by the auth state change listener
    } catch (error) {
      setAuthInProgress(false);
      throw error;
    }
    // Don't set authInProgress to false here - let the auth state change handle it
  };

  const logout = async () => {
    setLoading(true);
    try {
      await authService.signOut();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};